interface CognitoServiceConfig {
  clientId: string;
  hostedUiDomain: string;
  poolId: string;
  region: string;
  apiKey: string;
  identityProvider: string;
  idpIdentifier: string;
  tokenEndpoint: string;
}

type CognitoServiceKey =
  | "engage"
  | "myqueue"
  | "logocloud"
  | "transcribe";

type CognitoConfigByService = Record<CognitoServiceKey, CognitoServiceConfig>;

interface AppConfig {
  // The web-addin talks to several AIVA backends:
  //   - addinApiBaseUrl     → nonai-addin-api (channel/settings endpoints
  //     under /api/{channel}/settings; see Addin.Endpoints in the
  //     aiva-studio-nonai-backend project)
  //   - aiApiBaseUrl        → ai-api (Python FastAPI; /api/v1/chat,
  //     /api/v1/nba, /api/v1/config)
  //   - deckorateApiBaseUrl → deckorate-api, a separate deployment from
  //     ai-api (Deckorate's content endpoints, e.g. /content/speaker-notes)
  //   - docuparserApiBaseUrl → docuparser-nonai-backend. Like Deckorate,
  //     DocuParser is host-direct (no per-connector Cognito login) — it
  //     authenticates with the add-in's own AIVA Azure AD session, so its
  //     base URL is a plain env-configured value rather than something
  //     read off a Cognito connector's authConfig.
  // nonAiApiBaseUrl is exposed for parity with the studio-app SPA's
  // contract and so future web-addin calls into the WebPortal endpoints
  // (on the nonai-api) don't need another config refactor.
  nonAiApiBaseUrl: string;
  aiApiBaseUrl: string;
  deckorateApiBaseUrl: string;
  docuparserApiBaseUrl: string;
  addinApiBaseUrl: string;
  telemetryApiBaseUrl: string;
  oidcAuthority: string;
  oidcClientId: string;
  // AIVA-5640: OIDC scopes are no longer sourced from env vars. They are
  // managed per SSO config on the Studio SSO admin page and served via the
  // discovery endpoint; the OIDC dialog client (oidcClient.ts) uses that
  // list plus a fixed safe minimum. Do not re-add this field without moving
  // the whole flow off env vars again in lockstep.
  cognito: CognitoConfigByService;
}

interface CognitoRuntimeFields {
  CLIENT_ID?: string;
  HOSTED_UI_DOMAIN?: string;
  POOL_ID?: string;
  REGION?: string;
  API_KEY?: string;
  IDENTITY_PROVIDER?: string;
  IDP_IDENTIFIER?: string;
  TOKEN_ENDPOINT?: string;
}

declare global {
  var __CONFIG__: {
    NON_AI_API_BASE_URL?: string;
    AI_API_BASE_URL?: string;
    DECKORATE_API_BASE_URL?: string;
    DOCUPARSER_API_BASE_URL?: string;
    ADDIN_API_BASE_URL?: string;
    TELEMETRY_API_BASE_URL?: string;
    OIDC_AUTHORITY?: string;
    OIDC_CLIENT_ID?: string;
    COGNITO?: Partial<Record<Uppercase<CognitoServiceKey>, CognitoRuntimeFields>>;
  } | undefined;
}

function readCognitoServiceConfig(service: CognitoServiceKey): CognitoServiceConfig {
  const upper = service.toUpperCase() as Uppercase<CognitoServiceKey>;
  const runtime = globalThis.__CONFIG__?.COGNITO?.[upper];
  const env = import.meta.env as Record<string, string | undefined>;
  const prefix = `VITE_COGNITO_${upper}`;
  const pick = (rt: string | undefined, envKey: string): string =>
    rt ?? env[`${prefix}_${envKey}`] ?? "";

  return {
    clientId: pick(runtime?.CLIENT_ID, "CLIENT_ID"),
    hostedUiDomain: pick(runtime?.HOSTED_UI_DOMAIN, "HOSTED_UI_DOMAIN"),
    poolId: pick(runtime?.POOL_ID, "POOL_ID"),
    region: pick(runtime?.REGION, "REGION"),
    apiKey: pick(runtime?.API_KEY, "API_KEY"),
    identityProvider: pick(runtime?.IDENTITY_PROVIDER, "IDENTITY_PROVIDER"),
    idpIdentifier: pick(runtime?.IDP_IDENTIFIER, "IDP_IDENTIFIER"),
    tokenEndpoint: pick(runtime?.TOKEN_ENDPOINT, "TOKEN_ENDPOINT"),
  };
}

export const config: AppConfig = {
  nonAiApiBaseUrl: globalThis.__CONFIG__?.NON_AI_API_BASE_URL
    || import.meta.env.VITE_NON_AI_API_BASE_URL
    || "",
  aiApiBaseUrl: globalThis.__CONFIG__?.AI_API_BASE_URL
    || import.meta.env.VITE_AI_API_BASE_URL
    || "",
  deckorateApiBaseUrl: globalThis.__CONFIG__?.DECKORATE_API_BASE_URL
    || import.meta.env.VITE_DECKORATE_API_BASE_URL
    || "",
  docuparserApiBaseUrl: globalThis.__CONFIG__?.DOCUPARSER_API_BASE_URL
    || import.meta.env.VITE_DOCUPARSER_API_BASE_URL
    || "",
  addinApiBaseUrl: globalThis.__CONFIG__?.ADDIN_API_BASE_URL
    || import.meta.env.VITE_ADDIN_API_BASE_URL
    || "",
  telemetryApiBaseUrl: globalThis.__CONFIG__?.TELEMETRY_API_BASE_URL
    || import.meta.env.VITE_TELEMETRY_API_BASE_URL
    || "",
  oidcAuthority: globalThis.__CONFIG__?.OIDC_AUTHORITY
    || import.meta.env.VITE_OIDC_AUTHORITY
    || "",
  oidcClientId: globalThis.__CONFIG__?.OIDC_CLIENT_ID
    || import.meta.env.VITE_OIDC_CLIENT_ID
    || "",
  cognito: {
    engage: readCognitoServiceConfig("engage"),
    myqueue: readCognitoServiceConfig("myqueue"),
    logocloud: readCognitoServiceConfig("logocloud"),
    transcribe: readCognitoServiceConfig("transcribe"),
  },
};

export type { CognitoServiceConfig, CognitoServiceKey, CognitoConfigByService };
